/**
 * @fileoverview The Dashboard board (MOR-906 §7.8)
 *
 * Two groups of card that behave identically — drag to reorder (dnd-kit, as
 * in TaskStatusesManager), widen or shrink, remove — but are kept visually
 * apart because only one of them answers to the scope and period controls.
 *
 * The built-in metrics and chart sit inside a panel with those controls; the
 * saved widgets and tables sit below it, each running its own saved period.
 * Dragging works within a group, not across, since a saved card in the
 * overview panel would claim a period it does not use.
 *
 * Order spans both groups, so it cannot live on any one card: it is a list of
 * composite ids on the user's timesheet profile (convex/timesheets/board.ts).
 * Saved rows keep their own pin and width; the built-in cards, having no row,
 * keep theirs on the profile too.
 *
 * @component
 * @example
 * <FavouriteReportsBoard orgId={orgId} overview={cards} controls={<Toolbar />} />
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { BoardCardSize } from "@cvx/timesheets/timesheetProfiles/schema";

import { extractErrorMessage } from "@/utils/errors";
import { Spinner } from "@/ui/primitives/spinner";
import type { AnalyticsAnswer } from "./AskAnalyticsBox";
import { BoardPanelCard, BoardTileCard } from "./boardCard";
import {
  PivotCardBody,
  WidgetCardBody,
  type FavouritePivotReport,
  type FavouriteWidget,
} from "./boardBodies";
import {
  OVERVIEW_CARD_ORDER,
  defaultOverviewSize,
  type OverviewCard,
} from "./overviewCards";

type BoardItem =
  | {
      id: string;
      kind: "overview";
      rank: number;
      size: BoardCardSize;
      card: OverviewCard;
    }
  | {
      id: string;
      kind: "widget";
      rank: number;
      size: BoardCardSize;
      report: FavouriteWidget;
    }
  | {
      id: string;
      kind: "table";
      rank: number;
      size: BoardCardSize;
      report: FavouritePivotReport;
    };

/** A saved card as the builder takes it — the same shape an AI answer has. */
function asAnswer(
  item: Extract<BoardItem, { kind: "widget" | "table" }>,
): AnalyticsAnswer {
  return item.kind === "widget"
    ? { shape: "widget", title: item.report.name, spec: item.report.spec }
    : { shape: "pivot", title: item.report.name, spec: item.report.spec };
}

/** Pinned saved rows, in their legacy order — the tiebreak behind boardOrder. */
function pinned<
  T extends { isFavourite?: boolean; sortOrder?: number; name: string },
>(rows: T[] | undefined): T[] {
  return (rows ?? [])
    .filter((r) => r.isFavourite)
    .sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
    );
}

export function FavouriteReportsBoard({
  orgId,
  overview,
  overviewLoading,
  controls,
  onEdit,
}: {
  orgId: Id<"organizations">;
  /** The built-in cards, already computed. Empty while analytics loads. */
  overview: OverviewCard[];
  /** Analytics still reading — the panel keeps its controls and waits. */
  overviewLoading?: boolean;
  /** Scope and period controls, rendered at the top of the overview panel. */
  controls?: ReactNode;
  /**
   * Hand a saved card's spec back to the page, which reopens the builder on
   * it. Saving under the same name updates the card in place.
   */
  onEdit?: (answer: AnalyticsAnswer) => void;
}) {
  const { data: savedWidgets } = useQuery(
    convexQuery(api.timesheets.reportBuilder.listSaved, { orgId }),
  );
  const { data: savedTables } = useQuery(
    convexQuery(api.timesheets.pivotReports.listSaved, { orgId }),
  );
  const { data: context } = useQuery(
    convexQuery(api.timesheets.entries.entryContext, { orgId }),
  );
  const { data: board } = useQuery(
    convexQuery(api.timesheets.board.getBoard, { orgId }),
  );

  const setOverviewCard = useMutation(api.timesheets.board.setOverviewCard);
  const setBoardOrder = useMutation(api.timesheets.board.setBoardOrder);
  const setWidgetFavourite = useMutation(
    api.timesheets.reportBuilder.setFavourite,
  );
  const setWidgetSize = useMutation(api.timesheets.reportBuilder.setSize);
  const setTableFavourite = useMutation(
    api.timesheets.pivotReports.setFavourite,
  );
  const setTableSize = useMutation(api.timesheets.pivotReports.setSize);

  const overviewState = useMemo(
    () => new Map((board?.overviewCards ?? []).map((c) => [c.key, c])),
    [board?.overviewCards],
  );

  const items = useMemo<BoardItem[]>(() => {
    const widgets = pinned(savedWidgets ?? []);
    const tables = pinned(savedTables ?? []);

    // Kind decides the default arrangement — metrics, then what you built —
    // and boardOrder overrides it as soon as anything is dragged.
    const built: BoardItem[] = [];
    for (const card of overview) {
      const state = overviewState.get(card.key);
      if (state && !state.pinned) continue;
      built.push({
        id: `overview:${card.key}`,
        kind: "overview",
        rank: OVERVIEW_CARD_ORDER.indexOf(card.key),
        // A metric tile has no width to choose, so it ignores any width a
        // user set back when tiles could be widened.
        size: card.tile
          ? "small"
          : (state?.size ?? defaultOverviewSize(card.key)),
        card,
      });
    }
    widgets.forEach((report, i) =>
      built.push({
        id: `widget:${report._id}`,
        kind: "widget",
        rank: 100 + i,
        size: report.size ?? "half",
        report,
      }),
    );
    tables.forEach((report, i) =>
      built.push({
        id: `table:${report._id}`,
        kind: "table",
        rank: 200 + i,
        size: report.size ?? "half",
        report,
      }),
    );
    return built;
  }, [overview, overviewState, savedTables, savedWidgets]);

  // A card the user has never placed sorts after the ones they have.
  const arranged = useMemo(() => {
    const placed = board?.boardOrder ?? [];
    const indexOf = (id: string) => {
      const at = placed.indexOf(id);
      return at < 0 ? Number.MAX_SAFE_INTEGER : at;
    };
    return [...items].sort(
      (a, b) => indexOf(a.id) - indexOf(b.id) || a.rank - b.rank,
    );
  }, [board?.boardOrder, items]);

  // Local order for instant drag feedback; the server catches up.
  const [order, setOrder] = useState<string[]>([]);
  useEffect(() => {
    setOrder(arranged.map((i) => i.id));
  }, [arranged]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const isOverview = (id: string) => id.startsWith("overview:");

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = active.id as string;
    const overId = over.id as string;
    // The two groups are not interchangeable: only the overview panel is
    // governed by the scope and period above it.
    if (isOverview(activeId) !== isOverview(overId)) return;
    const from = order.indexOf(activeId);
    const to = order.indexOf(overId);
    if (from < 0 || to < 0) return;
    const next = arrayMove(order, from, to);
    setOrder(next);
    setBoardOrder({ orgId, orderedIds: next }).catch((err: unknown) =>
      toast.error(extractErrorMessage(err, "Could not save the order.")),
    );
  };

  const onError = (fallback: string) => (err: unknown) =>
    toast.error(extractErrorMessage(err, fallback));

  const resize = (item: BoardItem) => {
    const size: BoardCardSize = item.size === "full" ? "half" : "full";
    const saving =
      item.kind === "overview"
        ? setOverviewCard({ orgId, key: item.card.key, size })
        : item.kind === "widget"
          ? setWidgetSize({ reportId: item.report._id, size })
          : setTableSize({ reportId: item.report._id, size });
    saving.catch(onError("Could not resize the card."));
  };

  const remove = (item: BoardItem) => {
    if (item.kind === "overview") {
      setOverviewCard({ orgId, key: item.card.key, pinned: false }).catch(
        onError("Could not remove the card."),
      );
      return;
    }
    const saving =
      item.kind === "widget"
        ? setWidgetFavourite({ reportId: item.report._id, isFavourite: false })
        : setTableFavourite({ reportId: item.report._id, isFavourite: false });
    saving.catch(onError("Could not remove the card."));
  };

  const byId = new Map(arranged.map((i) => [i.id, i]));
  const inOrder = order
    .map((id) => byId.get(id))
    .filter((i): i is BoardItem => i !== undefined);
  const overviewItems = inOrder.filter((i) => i.kind === "overview");
  const savedItems = inOrder.filter((i) => i.kind !== "overview");
  const savedLoading =
    savedWidgets === undefined ||
    savedTables === undefined ||
    board === undefined;

  const renderCard = (item: BoardItem) => {
    const shared = {
      id: item.id,
      size: item.size,
      onRemove: () => remove(item),
    };
    if (item.kind === "overview") {
      const { card } = item;
      return card.tile ? (
        <BoardTileCard
          key={item.id}
          {...shared}
          title={card.title}
          removeLabel={`Remove ${card.title} from the board`}
          value={card.tile.value}
          sub={card.tile.sub}
        />
      ) : (
        <BoardPanelCard
          key={item.id}
          {...shared}
          onResize={() => resize(item)}
          title={card.title}
          removeLabel={`Remove ${card.title} from the board`}
        >
          {card.panel}
        </BoardPanelCard>
      );
    }
    return (
      <BoardPanelCard
        key={item.id}
        {...shared}
        onResize={() => resize(item)}
        onEdit={onEdit && (() => onEdit(asAnswer(item)))}
        title={item.report.name}
        removeLabel={`Unstar ${item.report.name}`}
      >
        {item.kind === "widget" ? (
          <WidgetCardBody
            orgId={orgId}
            report={item.report}
            categories={context?.categories}
          />
        ) : (
          <PivotCardBody orgId={orgId} report={item.report} />
        )}
      </BoardPanelCard>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-4">
        <section className="flex min-w-0 flex-col rounded-xl border border-border/70 bg-background/50 p-3">
          {controls && (
            <div className="mb-3 border-b border-border/60 pb-3">
              {controls}
            </div>
          )}
          {overviewLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner className="text-muted-foreground" />
            </div>
          ) : overviewItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No metrics on the board. Bring one back from the Cards menu.
            </p>
          ) : (
            <SortableContext
              items={overviewItems.map((i) => i.id)}
              strategy={rectSortingStrategy}
            >
              <Grid>{overviewItems.map(renderCard)}</Grid>
            </SortableContext>
          )}
        </section>

        {savedItems.length > 0 ? (
          <SortableContext
            items={savedItems.map((i) => i.id)}
            strategy={rectSortingStrategy}
          >
            <Grid>{savedItems.map(renderCard)}</Grid>
          </SortableContext>
        ) : (
          !savedLoading && (
            <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              Nothing pinned yet. Star a widget or table to keep it here.
            </p>
          )
        )}
      </div>
    </DndContext>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-w-0 grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {children}
    </div>
  );
}
